import React, { createContext, RefObject, useContext, useEffect, useMemo } from 'react'

import {
  useAlertingMetricsQuery,
  useAlertingSlackUserGroupsQuery,
  useRefreshAlertingSlackUserGroups,
} from 'modules/api/alerting/queries'
import { useUsersQuery } from 'modules/api/users/queries'
import {
  AlertingEntityType,
  AlertingMetric,
  FCWithChildren,
  SlackUserGroup,
  SpecialisationDataWithoutSystem,
  User,
} from 'types'
import { mergeQueryStatuses } from 'utils/queries'

export type QueueHealthPolicyContext = {
  userGroups: SlackUserGroup[]
  users: User[]
  metrics?: AlertingMetric[]
  scrollRef?: RefObject<HTMLDivElement | null>
  isThresholdPending: boolean
  isRefreshingUserGroups: boolean
}

export const QueueHealthPolicyContext = createContext<QueueHealthPolicyContext>(
  {} as QueueHealthPolicyContext,
)

export const useQueueHealthPolicy = () => {
  const context = useContext(QueueHealthPolicyContext)

  if (!context) {
    throw new Error(
      'useQueueHealthPolicy cannot be used outside QueueHealthPolicyContextProvider',
    )
  }
  return context
}

type QueueHealthPolicyProviderProps = {
  specialisation?: SpecialisationDataWithoutSystem
  scrollRef?: RefObject<HTMLDivElement | null>
}

export const QueueHealthPolicyProvider: FCWithChildren<
  QueueHealthPolicyProviderProps
> = ({ specialisation, scrollRef, children }) => {
  const { data: metrics, status: metricsStatus } = useAlertingMetricsQuery(
    {
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      entityId: specialisation?.id!,
      entityType: AlertingEntityType.Specialisation,
    },
    {
      enabled: Boolean(specialisation),
    },
  )
  const {
    data: userGroups = [],
    status: userGroupsStatus,
    refetch: refetchUserGroups,
  } = useAlertingSlackUserGroupsQuery()
  const { data: users = [], status: usersStatus } = useUsersQuery()
  const isThresholdPending =
    mergeQueryStatuses([metricsStatus, userGroupsStatus, usersStatus]) === 'pending'

  const { status: refreshAlertingSlackUserGroupsStatus, isLoading } =
    useRefreshAlertingSlackUserGroups()

  useEffect(() => {
    if (refreshAlertingSlackUserGroupsStatus !== 'success') {
      return
    }

    refetchUserGroups()
  }, [refreshAlertingSlackUserGroupsStatus, refetchUserGroups])

  const value = useMemo(
    () => ({
      metrics: metrics?.items ?? [],
      userGroups,
      users,
      scrollRef,
      isThresholdPending,
      isRefreshingUserGroups: isLoading,
    }),
    [metrics?.items, userGroups, users, isThresholdPending, isLoading, scrollRef],
  )

  return (
    <QueueHealthPolicyContext.Provider value={value}>
      {children}
    </QueueHealthPolicyContext.Provider>
  )
}
